/**
 * @file OverlayPlateFields.tsx
 * @description Плашка под надписью на фото (phase19.16, партия SB120, этап
 * 3а; uiux-sb120-stage3 §3 «Надписи на фото»):
 *
 * ```
 * Плашка  [Нет][Тёмная ✓][Светлая][⌖ Свой]          ← ColorField, ячейки-наборы
 * Плотность плашки ├────●─┤ [ 60 ] %          ↺ 60
 * ✓ Читается на любом фото (худшее 4,6:1)
 * ```
 *
 * Контраст — по худшему случаю: плашка на чёрном и на белом фото
 * (`overlayWorstContrast`). Без плашки — «читается не на каждом фото —
 * проверьте на канве». Предупреждение, не запрет.
 */

import { DEFAULT_SITE_THEME, SECTION_ELEMENT_WARNING_MESSAGES, SECTION_LOOK_LIMITS, SITE_CONTRAST_LEVEL_MARKS, formatContrast, type SectionOverlay } from "@chaiberi/shared";

import { NoticeStrip } from "../../../ui/NoticeStrip";
import { ColorField, type ColorTextValue } from "../../../site-fields/ColorField";
import { NumberSliderField } from "../../../site-fields/NumberSliderField";
import { useSectionForm } from "../section-form-context";
import { contrastView } from "../../../../lib/site-fields/color-contrast";
import {
  choosePlate,
  LIGHT_PLATE,
  overlayWorstContrast,
  plateChoice,
  plateCustomColor,
  PLATE_DENSITY_DEFAULT,
  setPlateColor,
  setPlateDensity,
} from "../../../../lib/site-builder/overlay-plate";

/** Ячейки-наборы плашки: подпись и образец. */
const CHOICES = [
  { id: "none", label: "Нет", color: "transparent" },
  { id: "dark", label: "Тёмная", color: "#000000" },
  { id: "light", label: "Светлая", color: LIGHT_PLATE },
] as const;

interface Props {
  overlay: SectionOverlay;
  onChange: (overlay: SectionOverlay) => void;
  /** Путь надписи: `columns.items.0.elements.1.overlays.0`. */
  path: string;
}

export function OverlayPlateFields({ overlay, onChange, path }: Props) {
  const { theme, preview } = useSectionForm();
  const colors = (theme ?? DEFAULT_SITE_THEME).colors;
  const choice = plateChoice(overlay);
  const custom = plateCustomColor(overlay, colors);
  const value: ColorTextValue<never> = custom ? { kind: "custom", color: custom } : { kind: "auto" };
  const worst = choice === "none" ? null : overlayWorstContrast(overlay, colors);
  const view = worst !== null ? contrastView(worst) : null;
  const P = SECTION_LOOK_LIMITS.overlayPlate;

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <ColorField<never>
        mode="text"
        label="Плашка"
        value={value}
        roles={[]}
        labels={{}}
        colors={colors}
        presets={CHOICES.map((c) => ({ ...c, active: choice === c.id, onSelect: () => onChange(choosePlate(overlay, c.id)) }))}
        onChange={(next) => (next.kind === "custom" ? onChange(setPlateColor(overlay, next.color)) : undefined)}
      />
      {choice !== "none" ? (
        <NumberSliderField
          label="Плотность плашки"
          value={overlay.plate?.density}
          inherit={{ value: PLATE_DENSITY_DEFAULT, label: `${PLATE_DENSITY_DEFAULT} %` }}
          min={P.min}
          max={P.max}
          step={1}
          unit="%"
          onChange={(density) => onChange(setPlateDensity(overlay, density))}
          onPreview={(v) => preview(`${path}.plate.density`, v)}
        />
      ) : null}
      {choice === "none" ? (
        <NoticeStrip tone="warning">{SECTION_ELEMENT_WARNING_MESSAGES.overlay_no_plate}</NoticeStrip>
      ) : view && view.level === "ok" ? (
        <p className="type-caption text-on-surface-variant">
          {SITE_CONTRAST_LEVEL_MARKS.ok} Читается на любом фото (худшее {formatContrast(worst ?? 0)}:1)
        </p>
      ) : view ? (
        <NoticeStrip tone="warning">Контраст на светлом или тёмном фото {view.label} — сделайте плашку плотнее или смените цвет текста.</NoticeStrip>
      ) : null}
    </div>
  );
}
